import {tx, ptx, usePageText} from '../lib/page-text';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { Modal, Code, EmptyState } from '../components/ui';
import { KeyIcon, PlusIcon } from '../components/icons';

const SCOPES = [
  { id: 'streams:read',     desc: 'Read stream metadata and status' },
  { id: 'streams:write',    desc: 'Create / update / delete streams + rotate keys' },
  { id: 'recordings:read',  desc: 'List recordings, fetch playback URLs' },
  { id: 'recordings:write', desc: 'Trigger recordings, delete VOD' },
  { id: 'webhooks:read',    desc: 'List webhook endpoints + delivery history' },
  { id: 'webhooks:write',   desc: 'Register / delete webhook endpoints' },
];

export function ApiKeysPage() {
  usePageText();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; fullKey: string } | null>(null);
  const list = useQuery({ queryKey: ['api-keys'], queryFn: async () => (await api.get('/me/api-keys')).data });
  const revoke = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/me/api-keys/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{tx("API keys")}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{tx("Programmatic access for your servers and CI")}</p>
        </div>
        <button className="btn lg" onClick={() => setShowCreate(true)}>{ptx("{v0} New API key", {v0: (<PlusIcon size={16}/>)})}</button>
      </div>

      {list.data && list.data.length === 0 ? (
        <EmptyState
          icon={<KeyIcon/>}
          title={tx("No API keys yet")}
          body={tx("Create a key to call the Kardolive API from your servers. Each key has scoped permissions.")}
          action={<button className="btn" onClick={() => setShowCreate(true)}>{ptx("{v0} New API key", {v0: (<PlusIcon/>)})}</button>}
        />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead><tr><th>{tx("Name")}</th><th>{tx("Prefix")}</th><th>{tx("Scopes")}</th><th>{tx("Created")}</th><th>{tx("Last used")}</th><th></th></tr></thead>
            <tbody>
              {list.data?.map((k: any) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{ptx("kl_live_{v0}…", {v0: (k.prefix)})}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {k.scopes.map((s: string) => <span key={s} className="badge">{s}</span>)}
                    </div>
                  </td>
                  <td className="muted">{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td className="muted">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : tx("never")}</td>
                  <td>
                    {k.revokedAt
                      ? <span className="badge warn">{tx("revoked")}</span>
                      : <button className="btn outline sm" onClick={() => { if (confirm(`Revoke "${k.name}"? This breaks any client using it.`)) revoke.mutate(k.id); }}>{tx("Revoke")}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateApiKeyModal onClose={() => setShowCreate(false)} onCreated={(data) => { setShowCreate(false); setRevealed(data); qc.invalidateQueries({ queryKey: ['api-keys'] }); }}/>}
      {revealed && <RevealKeyModal name={revealed.name} fullKey={revealed.fullKey} onClose={() => setRevealed(null)}/>}
    </div>
  );
}

function CreateApiKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (data: { name: string; fullKey: string }) => void }) {
  usePageText();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['streams:read', 'streams:write']);
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => (await api.post('/me/api-keys', { name, scopes })).data,
    onSuccess: (data) => onCreated({ name, fullKey: data.fullKey }),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });

  function toggle(scope: string) {
    setScopes((cur) => cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]);
  }

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New API key")}</h2>
      <p className="modal-subtitle">{tx("Pick the minimum scopes needed. Keys are shown only once.")}</p>
      <label className="label">{tx("Name")}</label>
      <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("e.g. CI publishing key")}/>
      <div style={{ height: 18 }}/>
      <label className="label">{tx("Scopes")}</label>
      <div className="card" style={{ padding: '4px 16px' }}>
        {SCOPES.map((s) => (
          <div key={s.id} className="toggle-row">
            <div className="label-block">
              <span className="name mono" style={{ fontFamily: 'ui-monospace, monospace' }}>{s.id}</span>
              <span className="desc">{s.desc}</span>
            </div>
            <button type="button" className={`toggle ${scopes.includes(s.id) ? 'on' : ''}`} onClick={() => toggle(s.id)}/>
          </div>
        ))}
      </div>
      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 20 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!name || scopes.length === 0 || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Generate key")}
        </button>
      </div>
    </Modal>
  );
}

function RevealKeyModal({ name, fullKey, onClose }: { name: string; fullKey: string; onClose: () => void }) {
  usePageText();
  return (
    <Modal onClose={onClose}>
      <h2>{tx("API key created")}</h2>
      <p className="modal-subtitle">{ptx("{v0} — it will not be shown again. Store it in your secret manager.", {v0: (<strong style={{ color: 'var(--warning)' }}>{tx("Save this key now")}</strong>)})}</p>
      <label className="label">{name}</label>
      <Code value={fullKey} large/>
      <div style={{ marginTop: 18 }}>
        <h3 style={{ marginBottom: 8, fontSize: 13 }}>{tx("Use it")}</h3>
        <pre className="code" style={{ fontSize: 12 }}>{`curl https://kardocloud.com/v1/me/streams \\
  -H "Authorization: Bearer ${fullKey}"`}</pre>
      </div>
      <div className="row-between" style={{ marginTop: 20 }}>
        <span/>
        <button className="btn" onClick={onClose}>{tx("I've saved it")}</button>
      </div>
    </Modal>
  );
}
